import * as Sentry from '@sentry/react';
import type { ErrorInfo } from 'react';
import APP_PACKAGE from '../../package.json';
import { env } from '@/config/env';

const DSN = 'https://829c72df1e95d80f507602eb645595b9@sentry.vnetwork.dev/3';

/** Initialize Sentry outside local dev (no DSN noise while developing). */
export function initSentry() {
  if (env.appEnv === 'development' && env.isDev) return;

  Sentry.init({
    dsn: DSN,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    environment: env.appEnv,
    release: `build-${APP_PACKAGE.version}.${env.appEnv}`,
    tracesSampleRate: env.appEnv === 'production' ? 1.0 : 0.1,
    replaysSessionSampleRate: env.appEnv === 'production' ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: true,
  });
}

/**
 * Attach the signed-in user to every Sentry event (id + email + name). Pass null
 * on logout to clear it. Called from AuthProvider so it covers ALL login paths
 * (local, SSO, invitation) AND session restore on reload — not just the login form.
 */
export function setSentryUser(
  user: { uuid: string; email?: string; name?: string } | null,
): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.uuid, email: user.email, username: user.name });
}

/**
 * Reporter for the @vnetwork/ui <ErrorBoundary onError={...}>. The boundary
 * only forwards caught render errors to its `onError` prop — without this,
 * crashes are swallowed by the fallback and never reach Sentry.
 */
export function reportErrorBoundary(error: Error, info: ErrorInfo) {
  Sentry.captureException(error, {
    contexts: { react: { componentStack: info.componentStack } },
  });
}
